import React, { useCallback, useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import Form from "@bappweb/form"
import Panel from "@bappweb/panel"
import TextField from "@bappweb/textfield"
import Button from "@bappweb/button"
import Checkbox from "@bappweb/checkbox"
import Link from "@bappweb/link"
import Captcha from "@smpu-web/nested/form/captcha"
import FormMessage from "@smpu-web/globalmodules/form-message/index"
import Page from "@smpu-web/globalmodules/page"
import Element from "@bappweb/element"
import { numeric, RegexpTypes, required, dateFormat } from "@bappweb/utils"
import { useGetTokenByTckn1Mutation, useGetCaptchaMutation, useRefreshCaptchaMutation, useApplyMutation } from "../../../api/services"
import { getDayMonthYearFromStr } from "@smpu-web/utils/utils"
import "../assets/style.scss"

const getCid = () => {
  let search = window.location.search;
  let params = new URLSearchParams(search);

  return params.get('cid') ?? "AkbOrgnc";
}

function MainForm() {
  const history = useHistory()

  const [identityNumber, setIdentityNumber] = useState<string>('')
  const [token, setToken] = useState<string>('')
  const [captchaValues, setCaptchaValues] = useState({
    captchaData: "",
    captcha: "",
    showCaptcha: false,
    captchaValidate: true
  })

  const [error, setError] = useState<string>()
  const [isFetching, setIsFetching] = useState(false)

  const [getCaptcha] = useGetCaptchaMutation();

  useEffect(() => {
    if (captchaValues.captchaData === "" && token !== "") {
      setIsFetching(true)
      getCaptcha({
        tckn: identityNumber,
        TokenSessionId: token
      })
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setError('')
            setCaptchaValues({
              ...captchaValues,
              showCaptcha: true,
              captchaData: payload.captchaData,
              captchaValidate: true
            })
          }
          else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => { setIsFetching(false) })
    }
  }, [captchaValues, getCaptcha, identityNumber, token])

  const [getTokenByTckn1] = useGetTokenByTckn1Mutation();

  const handleChange = useCallback((e) => {
    const name = e.target.name
    const value = e.target.value

    switch (name) {
      case "identityNumber":
        setIdentityNumber(value)
        setToken("")
        setCaptchaValues({
          ...captchaValues,
          captchaData: "",
          showCaptcha: false
        })
        if (value.length === 11) {
          setIsFetching(true)
          getTokenByTckn1(value)
            .unwrap()
            .then((payload) => {
              if (!payload.IsError) {
                setError('')
                setToken(payload.tokenSessionId)
              } else {
                setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              }
            })
            .finally(() => { setIsFetching(false) })
        }
        break;
      case "captcha":
        setCaptchaValues({
          ...captchaValues,
          captcha: value,
          captchaValidate: true
        })
        break;
      default:
        break;
    }
  }, [getTokenByTckn1, captchaValues])

  const [refreshCaptcha] = useRefreshCaptchaMutation();
  const handleCaptchaRefresh = useCallback(() => {
    setIsFetching(true)
    refreshCaptcha({
      tckn: identityNumber,
      TokenSessionId: token,
    })
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setError('')
          setCaptchaValues({
            ...captchaValues,
            captchaData: payload.captchaData,
            captchaValidate: true
          })
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => { setIsFetching(false) })
  }, [refreshCaptcha, identityNumber, captchaValues, token])

  const [apply] = useApplyMutation()

  const handleSubmit = useCallback(
    (e?: React.BaseSyntheticEvent, values?: Record<string, any>) => {
      if (values && values?.birthdate && values.captcha) {
        setIsFetching(true)
        const parsedBirthDate = getDayMonthYearFromStr(values.birthdate)
        let cid = getCid()
        apply({
          tckn: values.identityNumber,
          cepTel: "0090" + values.mobilePhone.replace(/\s/g, ''),
          dogumYil: parsedBirthDate.year,
          dogumAy: parsedBirthDate.month,
          dogumGun: parsedBirthDate.day,
          captcha: values.captcha.toUpperCase(),
          kvkk: values.checkboxKvkk,
          cid: cid,
          TokenSessionId: token,
        })
          .unwrap()
          .then((payload) => {
            if (!payload.IsError) {
              setError('')
              history.push({
                pathname: "/details/smsotp",
                state: payload
              })
            } else {
              if (payload.ErrorCode === 'COR-55550') {
                history.push('/result/invitetobranch')
              }
              setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              window.scrollTo({
                top: 0,
              });
              refreshCaptcha({
                tckn: identityNumber,
                TokenSessionId: token
              })
                .unwrap()
                .then((payload) => {
                  if (!payload.IsError) {
                    setCaptchaValues({
                      ...captchaValues,
                      captcha: "",
                      showCaptcha: true,
                      captchaData: payload.captchaData,
                      captchaValidate: false
                    })
                  }
                  else {
                    setError(payload.ErrorMessage ? payload.ErrorMessage : "")
                  }
                })
                .finally(() => { setIsFetching(false) })
            }
          })
          .finally(() => { setIsFetching(false) })
      }
    },
    [history, apply, captchaValues, identityNumber, refreshCaptcha, token]
  )

  return (
    <Page>
      <Form onSubmit={handleSubmit} onChange={handleChange} grid={false}>
        <Panel hasFooter connected={false}>
          <FormMessage className={"form-msg-error"} error={error}></FormMessage>
          <Panel.Body>
            <Element type="h3" className="bapp-panel-body-card-title">
              Merhaba!
            </Element>
            <Element type="h1" className="bapp-panel-body-card-description">
              Başvuru için kimlik ve iletişim bilgilerine ihtiyacımız var.
            </Element>
            <TextField
              label='TC Kimlik No'
              type="number"
              name="identityNumber"
              maxLength={11}
              value={identityNumber}
              validator={RegexpTypes.Numeric}
              validations={[required("TC Kimlik Numarası alanı zorunludur."), numeric()]}
            />
            <TextField
              label='Doğum Tarihi'
              type="text"
              placeholder="GG.AA.YYYY"
              mask="99.99.9999"
              name="birthdate"
              maxLength={10}
              validations={[
                required("Doğum Tarihi alanı zorunludur."),
                dateFormat("Lütfen Geçerli Bir Tarih Giriniz")
              ]}
            />
            <TextField
              label='Cep Telefon No'
              type="text"
              placeholder="5__ ___ __ __"
              mask="999 999 99 99"
              name="mobilePhone"
              maxLength={13}
              validations={[required("Cep Telefonu alanı zorunludur.")]}
            />
            {captchaValues.showCaptcha && (
              <Captcha
                label="Güvenlik Kodu"
                id="captcha"
                type="text"
                name="captcha"
                placeholder="Güvenlik Kodu"
                captcha={captchaValues.captchaData}
                value={captchaValues.captcha}
                maxLength={4}
                maxRefreshCount={3}
                refreshClick={handleCaptchaRefresh}
                validations={[required("Lütfen güvenlik kodunu giriniz.")]}
              />
            )}
            <Checkbox
              name="checkboxKvkk"
              validations={[required("Lütfen onay kutusunu işaretleyiniz.")]}
              className="cb-position-fix"
              validateOnSubmit={true}
              block
            >
              {"AKBANK T.A.Ş. olarak kişisel verilerini önemsiyoruz. 6698 nolu Kişisel Verilerin Korunması Kanunu kapsamında kişisel verilerinin işlenmesinden doğan haklarına ve bu konudaki detaylı bilgiye "}
              <Link
                href="//www.akbank.com/Documents/KISISELVERILERINKORUNMASI_TR.pdf"
                text={"aydınlatma metninden"}
                inline
                target="_blank"
              />
              {" ulaşabilirsin."}
            </Checkbox>
          </Panel.Body>
          <Panel.Footer>
            <Panel.RightFooter>
              <Button text="Hemen Başvur" type="submit" fetching={isFetching} disabled={isFetching}
                onClick={() =>
                  setCaptchaValues({
                    ...captchaValues,
                    captchaValidate: true
                  })
                } />
            </Panel.RightFooter>
          </Panel.Footer>
        </Panel>
      </Form>
    </Page>
  )
}

export default MainForm
